import { datadogLogs, type Logger } from '@datadog/browser-logs';
import { config } from '@/config';
import type { DecoratedProfile } from '@/contexts/CurrentUserContext';
import { getClientName } from '@/utils/getClientName';
import { redactSensitiveUrlsDeep } from './redactSensitiveData';

// A preview deploy is a production *build*, so it picks up the production
// client token and `config.env` resolves to 'production' — which is how 40+
// `*.pr.agendahero.com` hosts ended up in the production dataset, PR-only
// GraphQL 500s included. `config.datadog.enabled` excludes previews the same
// way `sentry.enabled` and `logrocket.enabled` do. Unlike Sentry there's no
// `enabled` option to pass through, so opting out means skipping `init`.
const isEnabled = config.datadog.enabled && Boolean(config.datadog.clientToken);

if (isEnabled) {
  datadogLogs.init({
    env: config.env,
    clientToken: config.datadog.clientToken,
    service: `client-${getClientName()}`,
    version: `${config.version}-${config.release}`,
    site: 'us3.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    silentMultipleInit: true,
    beforeSend: (event) => {
      const message = event.message.toLowerCase();

      if (
        message.includes('amplitude') ||
        message.includes('_ah_pixel_ratio_response')
      ) {
        return false;
      }
      // Every log carries the page URL (view.url), which on the reset page
      // holds the reset token.
      redactSensitiveUrlsDeep(event);
      return true;
    },
  });
}

const noop = () => {};

/**
 * Just the levels we call. Narrowing the export lets the disabled no-op satisfy
 * the type structurally rather than through a cast.
 */
type AppLogger = Pick<Logger, 'debug' | 'info' | 'warn' | 'error'>;

export const setUser = (user: DecoratedProfile) => {
  // Without `init` the SDK parks these in its pre-init buffer, so on a preview
  // deploy the name and email would sit in memory for a drain that never comes.
  if (!isEnabled || !user) {
    return;
  }

  datadogLogs.setUserProperty('id', user.id);

  if (user.name) {
    datadogLogs.setUserProperty('name', user.name);
  }
  if (user.email) {
    datadogLogs.setUserProperty('email', user.email);
  }
};

// `Logger.log` dispatches to the SDK's strategy unconditionally — the handler
// and level checks only decide whether a handling stack is captured — so the
// pre-init buffer would retain everything we passed it (magic prompts, extension
// payloads) awaiting a drain that never comes. `setHandler('silent')` doesn't
// help for the same reason; the export has to be a no-op when disabled.
export const logger: AppLogger = isEnabled
  ? datadogLogs.logger
  : { debug: noop, info: noop, warn: noop, error: noop };
